/**
 * Admin: Wipe EventInfo records (with the expanded filter API).
 *
 * Wraps the `wipeEvents` endpoint:
 *   - GET    -> dry run (counts only, never deletes)
 *   - DELETE -> bulk-deletes EventInfo / EventInfoMap / EventTitleAlias rows
 *               that match the supplied filters, plus matching documents in
 *               the Azure Cognitive Search index.
 *
 * Filters supported (all optional, AND-combined server-side):
 *   - startDateOlderThanDays  (default 90 when no filters supplied)
 *   - createdOlderThanDays
 *   - createdNewerThanDays
 *   - startDateBefore  (ISO date)
 *   - createdBefore    (ISO date)
 *   - createdAfter     (ISO date) — legacy
 *
 * UX: presets for the most common scenarios + a custom mode that lets the
 * admin combine filters freely. Always run dry-run first; the real DELETE
 * is gated by a typed-confirmation dialog.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Trash2, Search, AlertTriangle } from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { useAuth } from '../contexts/AuthContext';
import {
  previewWipeEvents,
  wipeEvents,
  saveLastRun,
  loadLastRun,
  type WipeEventsParams,
  type WipeEventsDryRunResponse,
  type WipeEventsDeleteResponse,
  type LastRunRecord,
} from '../services/adminApi';
import {
  Card,
  CardTitle,
  CardDescription,
  Banner,
  PrimaryButton,
  SecondaryButton,
  RunningIndicator,
  ConfirmDialog,
  StatGrid,
  Metric,
  RawJson,
  LastRunCard,
  SampleTable,
  ButtonRow,
  Field,
  Label,
  Input,
  Select,
} from '../components/admin/AdminUI';

const ACTION = 'wipeEvents';

type WipeRecord = WipeEventsDryRunResponse | WipeEventsDeleteResponse;

function isDeleteResponse(r: WipeRecord | null): r is WipeEventsDeleteResponse {
  return !!r && r.dryRun === false;
}

function totalRows(
  counts:
    | WipeEventsDeleteResponse['deleted']
    | WipeEventsDryRunResponse['wouldDelete']
    | undefined,
): number {
  if (!counts) return 0;
  return (
    (counts.eventInfo ?? 0) +
    (counts.eventInfoMap ?? 0) +
    (counts.eventTitleAlias ?? 0)
  );
}

// ---------------------------------------------------------------------------
// Filter mode presets
// ---------------------------------------------------------------------------

type FilterMode =
  | 'cleanupOldEvents'
  | 'rollbackRecentImport'
  | 'dropLegacyImports'
  | 'absoluteStartBefore'
  | 'absoluteCreatedBefore'
  | 'absoluteCreatedAfter'
  | 'custom'
  | 'wipeAll';

const MODE_LABELS: Record<FilterMode, string> = {
  cleanupOldEvents:       'Clean up old past events  (StartDate older than N days)',
  rollbackRecentImport:   'Roll back recent import   (DateCreated newer than N days)',
  dropLegacyImports:      'Drop legacy events        (DateCreated older than N days)',
  absoluteStartBefore:    'Absolute: StartDate before <date>',
  absoluteCreatedBefore:  'Absolute: DateCreated before <date>',
  absoluteCreatedAfter:   'Legacy: DateCreated after <date>',
  custom:                 'Custom — combine multiple filters',
  wipeAll:                '⚠ Wipe ALL events (no filters)',
};

const MODE_DESCRIPTIONS: Record<FilterMode, string> = {
  cleanupOldEvents:
    'Removes events whose StartDate is more than N days ago. Safe default — leave the field blank to use the server default of 90 days, or set your own.',
  rollbackRecentImport:
    'Removes events imported in the last N days. Use this to undo a bad import batch.',
  dropLegacyImports:
    'Removes events imported more than N days ago. Useful to drop legacy data with mismatched fields while keeping recent imports.',
  absoluteStartBefore:
    'Removes events whose StartDate is before the supplied date. ISO date or full ISO timestamp.',
  absoluteCreatedBefore:
    'Removes events whose DateCreated is before the supplied date.',
  absoluteCreatedAfter:
    'Legacy filter — removes events whose DateCreated is AFTER the supplied date. Empty cutoff wipes everything.',
  custom:
    'Combine any subset of the supported filters. All filters are AND-joined server-side.',
  wipeAll:
    'No filters — every event in the database. This bypasses the server\'s safe default by setting createdAfter=2000-01-01.',
};

function parseNum(s: string): number | undefined {
  if (s.trim() === '') return undefined;
  const n = Number(s);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function describeFilters(p: WipeEventsParams): string {
  const parts: string[] = [];
  if (p.startDateOlderThanDays != null) parts.push(`startDateOlderThanDays=${p.startDateOlderThanDays}`);
  if (p.createdOlderThanDays != null) parts.push(`createdOlderThanDays=${p.createdOlderThanDays}`);
  if (p.createdNewerThanDays != null) parts.push(`createdNewerThanDays=${p.createdNewerThanDays}`);
  if (p.startDateBefore) parts.push(`startDateBefore=${p.startDateBefore}`);
  if (p.createdBefore) parts.push(`createdBefore=${p.createdBefore}`);
  if (p.createdAfter) parts.push(`createdAfter=${p.createdAfter}`);
  return parts.length ? parts.join(' & ') : '(none — server default applies)';
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const AdminWipeEventsPage: React.FC = () => {
  const { userInfo } = useAuth();

  const [mode, setMode] = useState<FilterMode>('cleanupOldEvents');

  // Per-mode value fields.
  const [startDateOlderThanDays, setStartDateOlderThanDays] = useState<string>('');
  const [createdNewerThanDays, setCreatedNewerThanDays] = useState<string>('1');
  const [createdOlderThanDays, setCreatedOlderThanDays] = useState<string>('180');
  const [startDateBefore, setStartDateBefore] = useState<string>('');
  const [createdBefore, setCreatedBefore] = useState<string>('');
  const [createdAfter, setCreatedAfter] = useState<string>('');

  // Custom mode: combine multiple at once.
  const [customStartOlder, setCustomStartOlder] = useState<string>('');
  const [customCreatedOlder, setCustomCreatedOlder] = useState<string>('');
  const [customCreatedNewer, setCustomCreatedNewer] = useState<string>('');
  const [customStartBefore, setCustomStartBefore] = useState<string>('');
  const [customCreatedBefore, setCustomCreatedBefore] = useState<string>('');
  const [customCreatedAfter, setCustomCreatedAfter] = useState<string>('');

  const [preview, setPreview] = useState<WipeEventsDryRunResponse | null>(null);
  const [result, setResult] = useState<WipeEventsDeleteResponse | null>(null);
  const [previewedAt, setPreviewedAt] = useState<Date | null>(null);
  const [running, setRunning] = useState(false);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastRun, setLastRun] = useState<LastRunRecord<WipeEventsDeleteResponse> | null>(null);

  useEffect(() => { setLastRun(loadLastRun(ACTION)); }, []);

  // Reset preview/result on mode change.
  useEffect(() => {
    setPreview(null);
    setResult(null);
    setPreviewedAt(null);
    setError(null);
  }, [mode]);

  const params: WipeEventsParams = useMemo(() => {
    switch (mode) {
      case 'cleanupOldEvents':
        return { startDateOlderThanDays: parseNum(startDateOlderThanDays) };
      case 'rollbackRecentImport':
        return { createdNewerThanDays: parseNum(createdNewerThanDays) };
      case 'dropLegacyImports':
        return { createdOlderThanDays: parseNum(createdOlderThanDays) };
      case 'absoluteStartBefore':
        return { startDateBefore: startDateBefore.trim() || undefined };
      case 'absoluteCreatedBefore':
        return { createdBefore: createdBefore.trim() || undefined };
      case 'absoluteCreatedAfter':
        return { createdAfter: createdAfter.trim() || undefined };
      case 'wipeAll':
        return { createdAfter: '2000-01-01' };
      case 'custom':
        return {
          startDateOlderThanDays: parseNum(customStartOlder),
          createdOlderThanDays: parseNum(customCreatedOlder),
          createdNewerThanDays: parseNum(customCreatedNewer),
          startDateBefore: customStartBefore.trim() || undefined,
          createdBefore: customCreatedBefore.trim() || undefined,
          createdAfter: customCreatedAfter.trim() || undefined,
        };
    }
  }, [
    mode,
    startDateOlderThanDays,
    createdNewerThanDays,
    createdOlderThanDays,
    startDateBefore,
    createdBefore,
    createdAfter,
    customStartOlder,
    customCreatedOlder,
    customCreatedNewer,
    customStartBefore,
    customCreatedBefore,
    customCreatedAfter,
  ]);

  const customHasAny = mode !== 'custom' || Object.values(params).some(
    (v) => v !== undefined && v !== '',
  );

  const isWipeAll = mode === 'wipeAll';

  const doPreview = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    setStartedAt(Date.now());
    try {
      const data = await previewWipeEvents(params);
      setPreview(data);
      setPreviewedAt(new Date());
    } catch (e: any) {
      setError(e?.message ?? 'Preview failed');
    } finally {
      setRunning(false);
      setStartedAt(null);
    }
  };

  const doDelete = async () => {
    setConfirmOpen(false);
    setRunning(true);
    setError(null);
    setStartedAt(Date.now());
    try {
      const data = await wipeEvents(params);
      setResult(data);
      setPreview(null);
      const record: LastRunRecord<WipeEventsDeleteResponse> = {
        timestamp: new Date().toISOString(),
        triggeredBy: userInfo.userName || userInfo.userId,
        summary: {
          mode,
          filters: describeFilters(params),
          eventInfo: data.deleted?.eventInfo ?? 0,
          eventInfoMap: data.deleted?.eventInfoMap ?? 0,
          eventTitleAlias: data.deleted?.eventTitleAlias ?? 0,
          searchDocs: data.deleted?.searchDocs ?? 0,
          searchFailed: data.searchIndex?.failed ?? 0,
        },
        response: data,
      };
      saveLastRun(ACTION, record);
      setLastRun(record);
    } catch (e: any) {
      setError(e?.message ?? 'Wipe failed');
    } finally {
      setRunning(false);
      setStartedAt(null);
    }
  };

  const counts = isDeleteResponse(result) ? result.deleted : preview?.wouldDelete;
  const totalDbRows = totalRows(counts);
  const totalSearchDocs = counts?.searchDocs ?? 0;
  const echoedFilters = isDeleteResponse(result) ? result.filters : preview?.filters;
  const whereClause = isDeleteResponse(result) ? result.whereClause : preview?.whereClause;
  const defaultsApplied =
    isDeleteResponse(result) ? result.defaultsApplied : preview?.defaultsApplied;

  const renderFilterEditor = () => {
    switch (mode) {
      case 'cleanupOldEvents':
        return (
          <Field>
            <Label htmlFor="startDateOlderThanDays">
              StartDate older than (days) — leave empty to use server default (90)
            </Label>
            <Input
              id="startDateOlderThanDays"
              type="number"
              min={1}
              placeholder="90"
              value={startDateOlderThanDays}
              onChange={(e) => setStartDateOlderThanDays(e.target.value)}
              disabled={running}
            />
          </Field>
        );
      case 'rollbackRecentImport':
        return (
          <Field>
            <Label htmlFor="createdNewerThanDays">DateCreated newer than (days)</Label>
            <Input
              id="createdNewerThanDays"
              type="number"
              min={1}
              placeholder="1"
              value={createdNewerThanDays}
              onChange={(e) => setCreatedNewerThanDays(e.target.value)}
              disabled={running}
            />
          </Field>
        );
      case 'dropLegacyImports':
        return (
          <Field>
            <Label htmlFor="createdOlderThanDays">DateCreated older than (days)</Label>
            <Input
              id="createdOlderThanDays"
              type="number"
              min={1}
              placeholder="180"
              value={createdOlderThanDays}
              onChange={(e) => setCreatedOlderThanDays(e.target.value)}
              disabled={running}
            />
          </Field>
        );
      case 'absoluteStartBefore':
        return (
          <Field>
            <Label htmlFor="startDateBefore">StartDate before (ISO date or timestamp)</Label>
            <Input
              id="startDateBefore"
              type="text"
              placeholder="e.g. 2025-01-01 or 2025-01-01T00:00:00Z"
              value={startDateBefore}
              onChange={(e) => setStartDateBefore(e.target.value)}
              disabled={running}
            />
          </Field>
        );
      case 'absoluteCreatedBefore':
        return (
          <Field>
            <Label htmlFor="createdBefore">DateCreated before (ISO date or timestamp)</Label>
            <Input
              id="createdBefore"
              type="text"
              placeholder="e.g. 2025-06-01"
              value={createdBefore}
              onChange={(e) => setCreatedBefore(e.target.value)}
              disabled={running}
            />
          </Field>
        );
      case 'absoluteCreatedAfter':
        return (
          <Field>
            <Label htmlFor="createdAfter">DateCreated after (ISO date or timestamp)</Label>
            <Input
              id="createdAfter"
              type="text"
              placeholder="e.g. 2026-04-01"
              value={createdAfter}
              onChange={(e) => setCreatedAfter(e.target.value)}
              disabled={running}
            />
          </Field>
        );
      case 'wipeAll':
        return null;
      case 'custom':
        return (
          <>
            <CardDescription>
              Fill in any subset below — empty fields are ignored. All supplied
              filters are AND-joined on the server.
            </CardDescription>
            <Field>
              <Label>StartDate older than (days)</Label>
              <Input
                type="number"
                min={1}
                value={customStartOlder}
                onChange={(e) => setCustomStartOlder(e.target.value)}
                disabled={running}
              />
            </Field>
            <Field>
              <Label>DateCreated older than (days)</Label>
              <Input
                type="number"
                min={1}
                value={customCreatedOlder}
                onChange={(e) => setCustomCreatedOlder(e.target.value)}
                disabled={running}
              />
            </Field>
            <Field>
              <Label>DateCreated newer than (days)</Label>
              <Input
                type="number"
                min={1}
                value={customCreatedNewer}
                onChange={(e) => setCustomCreatedNewer(e.target.value)}
                disabled={running}
              />
            </Field>
            <Field>
              <Label>StartDate before (ISO)</Label>
              <Input
                type="text"
                placeholder="2025-01-01"
                value={customStartBefore}
                onChange={(e) => setCustomStartBefore(e.target.value)}
                disabled={running}
              />
            </Field>
            <Field>
              <Label>DateCreated before (ISO)</Label>
              <Input
                type="text"
                placeholder="2025-01-01"
                value={customCreatedBefore}
                onChange={(e) => setCustomCreatedBefore(e.target.value)}
                disabled={running}
              />
            </Field>
            <Field>
              <Label>DateCreated after (ISO)</Label>
              <Input
                type="text"
                placeholder="2026-04-01"
                value={customCreatedAfter}
                onChange={(e) => setCustomCreatedAfter(e.target.value)}
                disabled={running}
              />
            </Field>
          </>
        );
    }
  };

  return (
    <AdminLayout
      currentPage="wipeEvents"
      pageTitle="Wipe Events"
      pageDescription="Bulk-delete EventInfo / EventInfoMap / EventTitleAlias rows matching a flexible date filter — and prune the matching Azure Cognitive Search documents."
    >
      <Banner tone="warning">
        <strong>⚠ Destructive operation.</strong> This permanently deletes
        rows from <code>EventInfo</code>, <code>EventInfoMap</code>, and{' '}
        <code>EventTitleAlias</code>, plus any matching documents in the
        search index. <em>No other tables are touched</em>. Always run a
        dry-run preview first.
      </Banner>

      <Card>
        <CardTitle>Filter mode</CardTitle>
        <CardDescription>
          Choose the wipe scenario. The server AND-combines all supplied
          filters; if none are sent, it applies the safe default
          (<code>startDateOlderThanDays=90</code>).
        </CardDescription>
        <Field>
          <Label htmlFor="mode">Mode</Label>
          <Select
            id="mode"
            value={mode}
            onChange={(e) => setMode(e.target.value as FilterMode)}
            disabled={running}
          >
            {(Object.keys(MODE_LABELS) as FilterMode[]).map((m) => (
              <option key={m} value={m}>{MODE_LABELS[m]}</option>
            ))}
          </Select>
        </Field>
        <Banner tone="info">{MODE_DESCRIPTIONS[mode]}</Banner>

        {renderFilterEditor()}

        <CardDescription style={{ marginTop: 8 }}>
          <strong>Filters that will be sent:</strong>{' '}
          <code>{describeFilters(params)}</code>
        </CardDescription>

        <ButtonRow>
          <PrimaryButton
            onClick={doPreview}
            disabled={running || (mode === 'custom' && !customHasAny)}
          >
            <Search size={16} />
            Preview (dry run)
          </PrimaryButton>
          {previewedAt && !running && (
            <span style={{ fontSize: 13, color: '#595959' }}>
              Last preview: {previewedAt.toLocaleTimeString()}
            </span>
          )}
        </ButtonRow>
        {running && startedAt != null && (
          <RunningIndicator
            startedAt={startedAt}
            message={
              isDeleteResponse(result) || confirmOpen
                ? 'Deleting events and pruning search index…'
                : 'Counting rows that would be deleted…'
            }
          />
        )}
        {error && <Banner tone="error">{error}</Banner>}
      </Card>

      {(preview || result) && (
        <Card>
          <CardTitle>
            {isDeleteResponse(result) ? 'Wipe results' : 'Dry-run preview'}
          </CardTitle>
          <CardDescription>
            <strong>Filters:</strong>{' '}
            <code>
              {echoedFilters
                ? Object.entries(echoedFilters)
                    .filter(([k]) => !k.endsWith('_cutoff'))
                    .map(([k, v]) => `${k}=${v}`)
                    .join(' & ') || '(none)'
                : describeFilters(params)}
            </code>
            {defaultsApplied && (
              <>
                {' '}
                <em>(server default applied — no filters were supplied)</em>
              </>
            )}
          </CardDescription>

          {whereClause && (
            <CardDescription>
              <strong>SQL where clause:</strong>{' '}
              <code style={{ fontFamily: 'monospace' }}>{whereClause}</code>
            </CardDescription>
          )}

          {echoedFilters &&
            Object.keys(echoedFilters).some((k) => k.endsWith('_cutoff')) && (
              <CardDescription>
                {Object.entries(echoedFilters)
                  .filter(([k]) => k.endsWith('_cutoff'))
                  .map(([k, v]) => (
                    <div key={k}>
                      <strong>{k.replace('_cutoff', '')} resolves to:</strong>{' '}
                      <code>{String(v)}</code>
                    </div>
                  ))}
              </CardDescription>
            )}

          {isWipeAll && !isDeleteResponse(result) && (
            <Banner tone="error">
              <AlertTriangle size={16} style={{ verticalAlign: 'middle' }} />{' '}
              "Wipe ALL" mode selected — this would delete{' '}
              <strong>every</strong> event in the database.
            </Banner>
          )}

          <StatGrid>
            <Metric
              label="EventInfo"
              value={(counts?.eventInfo ?? 0).toLocaleString()}
              tone={(counts?.eventInfo ?? 0) > 0 ? 'warning' : 'neutral'}
            />
            <Metric
              label="EventInfoMap"
              value={(counts?.eventInfoMap ?? 0).toLocaleString()}
            />
            <Metric
              label="EventTitleAlias"
              value={(counts?.eventTitleAlias ?? 0).toLocaleString()}
            />
            <Metric
              label="Search docs"
              value={totalSearchDocs.toLocaleString()}
              hint={
                preview?.searchPreviewError
                  ? `⚠ ${preview.searchPreviewError}`
                  : undefined
              }
              tone={preview?.searchPreviewError ? 'warning' : 'neutral'}
            />
            <Metric
              label="Total DB rows"
              value={totalDbRows.toLocaleString()}
              tone={totalDbRows > 0 ? 'warning' : 'success'}
            />
            <Metric
              label="Mode"
              value={isDeleteResponse(result) ? 'Deleted' : 'Dry run'}
              tone={isDeleteResponse(result) ? 'success' : 'neutral'}
            />
          </StatGrid>

          {isDeleteResponse(result) && result.searchIndex && (
            <>
              <CardTitle style={{ fontSize: 16, marginTop: 16 }}>
                Search index pruning
              </CardTitle>
              <StatGrid>
                <Metric
                  label="Matched"
                  value={(result.searchIndex.matched ?? 0).toLocaleString()}
                />
                <Metric
                  label="Deleted"
                  value={(result.searchIndex.deleted ?? 0).toLocaleString()}
                  tone="success"
                />
                <Metric
                  label="Failed"
                  value={(result.searchIndex.failed ?? 0).toLocaleString()}
                  tone={
                    (result.searchIndex.failed ?? 0) > 0 ? 'error' : 'neutral'
                  }
                />
              </StatGrid>
              {Array.isArray(result.searchIndex.errors) &&
                result.searchIndex.errors.length > 0 && (
                  <>
                    <Banner tone="warning">
                      One or more search-index batches failed. The DB delete
                      was <strong>not</strong> rolled back. Run{' '}
                      <code>cleanupOrphanedDocs</code> to recover.
                    </Banner>
                    <SampleTable>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.searchIndex.errors.map((m, i) => (
                          <tr key={i}>
                            <td>{i + 1}</td>
                            <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                              {m}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </SampleTable>
                  </>
                )}
            </>
          )}

          {!isDeleteResponse(result) && preview?.note && (
            <Banner tone="info">{preview.note}</Banner>
          )}
          {!isDeleteResponse(result) && preview?.message && (
            <Banner tone="info">{preview.message}</Banner>
          )}
          {isDeleteResponse(result) && result.message && (
            <Banner tone="success">{result.message}</Banner>
          )}

          <ButtonRow>
            {!isDeleteResponse(result) && (
              <PrimaryButton
                onClick={() => setConfirmOpen(true)}
                disabled={totalDbRows === 0 || running}
                style={{ background: totalDbRows > 0 ? '#cf1322' : undefined }}
              >
                <Trash2 size={16} />
                Wipe {totalDbRows.toLocaleString()} row
                {totalDbRows === 1 ? '' : 's'}
              </PrimaryButton>
            )}
            <SecondaryButton onClick={doPreview} disabled={running}>
              <Search size={16} />
              {isDeleteResponse(result) ? 'Run another preview' : 'Re-preview'}
            </SecondaryButton>
          </ButtonRow>

          <RawJson data={(result ?? preview) as object} />
        </Card>
      )}

      <LastRunCard record={lastRun} />

      <ConfirmDialog
        open={confirmOpen}
        title="Wipe events"
        body={
          <>
            <p>
              You're about to delete <strong>{totalDbRows.toLocaleString()}</strong>{' '}
              database row{totalDbRows === 1 ? '' : 's'} and{' '}
              <strong>{totalSearchDocs.toLocaleString()}</strong> search
              document{totalSearchDocs === 1 ? '' : 's'}:
            </p>
            <ul style={{ marginTop: 8, marginBottom: 12 }}>
              <li>
                <strong>{(counts?.eventInfo ?? 0).toLocaleString()}</strong>{' '}
                <code>EventInfo</code> row
                {(counts?.eventInfo ?? 0) === 1 ? '' : 's'}
              </li>
              <li>
                <strong>{(counts?.eventInfoMap ?? 0).toLocaleString()}</strong>{' '}
                <code>EventInfoMap</code> row
                {(counts?.eventInfoMap ?? 0) === 1 ? '' : 's'}
              </li>
              <li>
                <strong>{(counts?.eventTitleAlias ?? 0).toLocaleString()}</strong>{' '}
                <code>EventTitleAlias</code> row
                {(counts?.eventTitleAlias ?? 0) === 1 ? '' : 's'}
              </li>
              <li>
                <strong>{totalSearchDocs.toLocaleString()}</strong> Azure
                Cognitive Search document{totalSearchDocs === 1 ? '' : 's'}
              </li>
            </ul>
            <p>
              <strong>Mode:</strong> {MODE_LABELS[mode]}
            </p>
            <p>
              <strong>Filters:</strong>{' '}
              <code>{describeFilters(params)}</code>
            </p>
            {isWipeAll && (
              <p style={{ color: '#cf1322', fontWeight: 600 }}>
                ⚠ "Wipe ALL" mode — every event in the database will be deleted.
              </p>
            )}
            <p>This cannot be undone.</p>
          </>
        }
        promptText={
          <>
            Type <code>{totalDbRows}</code> (the total DB row count) to confirm:
          </>
        }
        confirmPhrase={String(totalDbRows)}
        primaryLabel={`Wipe ${totalDbRows.toLocaleString()} row${totalDbRows === 1 ? '' : 's'}`}
        onConfirm={doDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </AdminLayout>
  );
};

export default AdminWipeEventsPage;
